import { useClientStore } from "../stores/client";
import { withErrorHandling } from "../utils/errors";

interface QueryProps {
  defaultErrorMessage?: string;
  endpoint: string;
  paginated?: boolean;
}

interface QueryParams {
  page?: number;
  page_size?: number | string;
  [key: string]: unknown;
}

const useGetQueryFn = <Response = unknown>({
  defaultErrorMessage = 'An unexpected error occurred while fetching data',
  endpoint,
  paginated = false,
}: QueryProps) => {
  const request = useClientStore((state) => state.request);

  const queryFn = withErrorHandling(
    async (paramsInput: QueryParams = {}): Promise<Response> => {

      let rawParams: Record<string, unknown> = { ...paramsInput };

      // Only apply pagination if enabled
      if (paginated) {
        const {
          page = 1,
          page_size = 10,
          ...rest
        } = paramsInput;

        rawParams = {
          page,
          page_size,
          ...rest,
        };
      }

      // Clean params
      const params = Object.entries(rawParams).reduce<Record<string, string>>(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[key] = String(value);
          }
          return acc;
        },
        {}
      );

      const searchParams = new URLSearchParams(params);

      // Ignore trailing "?" if no params
      const queryString = searchParams.toString();
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await request<Response>(url, 'GET');

      return response;
    },
    defaultErrorMessage
  );

  return queryFn;
};

export default useGetQueryFn;